// ─────────────────────────────────────────────
//  SetupPage heavy tabs — extracted for file-size compliance (Plan 13).
//  Exports: RoomsTab, UsersTab, PackageTab, NotificationsTab, ROLE_OPTIONS.
//  All token-based (no legacy dark hex values).
// ─────────────────────────────────────────────
import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase/config";
import { collection, onSnapshot, query, where, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "../firebase/sdk";
import { updateUser, createUser, mirrorRoleToStaff, addStaff } from "../firebase/firestore";
import { staffRoleForProfile } from "../domain/staff";
import { createUserAccount } from "../firebase/auth";
import { bulkCreateLoginAccounts, needsAccount } from "../firebase/bulkAccounts";
import { linkStaffToAccounts } from "../firebase/linkStaff";
import { staffEmail } from "../domain/email";
import { seedProviderCodes } from "../firebase/seedProviderCodes";
import { seedStaffCodes } from "../firebase/seedStaffCodes";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useFacility } from "../hooks/useFacility";

// The allowance a facility with no `maxRooms` of its own falls back to — the
// number that used to be hardcoded inside the banner.
const DEFAULT_ROOM_LIMIT = 30;
import { useLocations } from "../hooks/useLocations";
import { labelFor, canonicalKey } from "../domain/locations";
import { summarizePresence } from "../domain/presence";
import tokens from "../theme/tokens";
import s from "./setupPage.styles";
import Modal from "../components/Modal";
import Field from "../components/Field";

const { color } = tokens;

// ── TAB: ROOMS ────────────────────────────────
export function RoomsTab({ facilityId: facilityIdProp }) {
  const { user } = useAuth();
  const toast = useToast();
  // Prefer the shared setup selector (SetupPage topbar); fall back to the
  // user's active facility. Rooms are facility-specific.
  const facilityId = facilityIdProp || user?.activeFacilityId || "default";
  const { active: activeLocs, locations } = useLocations(facilityId);
  // The plan allowance for THIS facility. `facilities/{id}.maxRooms` has existed
  // since seed.js and nothing read it, so the banner was a build-level constant
  // that would be wrong for the first tenant with a different allowance.
  const { facility } = useFacility(facilityId);
  const [rooms, setRooms]     = useState([]);
  // ⚠️ OPENED BY `?add=1`, so a link can land on the form instead of a page the
  // operator then has to hunt through. The Timeline's "New room" button was that
  // caller until 2026-09-10, when the owner had it removed; the parameter stays
  // because it is a property of this tab, not of the button that used it, and
  // any future link is entitled to it. Read once at mount: re-reading it would
  // spring the form open again every time the tab re-rendered, and the URL keeps
  // the parameter.
  const [showAdd, setShowAdd] = useState(
    () => new URLSearchParams(window.location.search).get("add") === "1"
  );
  const [newName, setNewName] = useState("");
  const [newLoc, setNewLoc]   = useState("");
  const [saving, setSaving]   = useState(false);
  const [editId, setEditId]   = useState(null);
  const [editName, setEditName] = useState("");
  const [editLoc, setEditLoc]   = useState("");

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "rooms"), where("facilityId", "==", facilityId)),
      snap => {
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a,b) => a.name.localeCompare(b.name, undefined, {numeric:true}));
        setRooms(data);
      }
    );
    return unsub;
  }, [facilityId]);

  const handleAddRoom = async () => {
    if (!newName.trim()) { toast.error("Enter a room name."); return; }
    setSaving(true);
    try {
      await addDoc(collection(db, "rooms"), {
        facilityId, name: newName.trim(),
        location: newLoc || activeLocs[0]?.key || "other",
        status: "empty", providerId: null, supervisingDoctorId: null,
        comments: "", isActive: true, onBreak: false,
        updatedAt: serverTimestamp(),
      });
      toast.success(`Room "${newName.trim()}" added.`);
      setNewName(""); setNewLoc(""); setShowAdd(false);
    } catch (err) {
      console.error("Add room failed:", err);
      toast.error("Could not add the room.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (room) => {
    try {
      await updateDoc(doc(db, "rooms", room.id), { isActive: !room.isActive });
    } catch (err) {
      console.error("Toggle room failed:", err);
      toast.error("Could not change the room's status.");
    }
  };

  const removeRoom = async (room) => {
    if (!window.confirm(`Remove "${room.name}" permanently? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, "rooms", room.id));
      toast.info(`Room "${room.name}" removed.`);
    } catch (err) {
      console.error("Remove room failed:", err);
      toast.error("Could not remove the room.");
    }
  };

  const locOptionsWith = (current) => {
    const opts = activeLocs.map(l => ({ value: l.key, label: l.label }));
    const canon = canonicalKey(current);
    if (current && !opts.some(o => o.value === canon || o.value === current)) {
      opts.push({ value: canon, label: labelFor(locations, current) });
    }
    return opts;
  };

  const startEdit = (room) => {
    setEditId(room.id);
    setEditName(room.name);
    setEditLoc(canonicalKey(room.location));
  };

  const saveEdit = async () => {
    if (!editName.trim()) return;
    try {
      await updateDoc(doc(db, "rooms", editId), {
        name: editName.trim(),
        location: editLoc,
        updatedAt: serverTimestamp(),
      });
      toast.success("Room updated.");
      setEditId(null);
    } catch (err) {
      console.error("Edit room failed:", err);
      toast.error("Could not save the room.");
    }
  };

  // Group rooms into a box per location. Active locations always get a box
  // (even when empty) so the add-room flow has a visible target; an inactive
  // or unknown location that still has rooms also gets one.
  const groups = useMemo(() => {
    const byKey = new Map();
    activeLocs.forEach(l => byKey.set(l.key, []));
    rooms.forEach(r => {
      const k = canonicalKey(r.location);
      if (!byKey.has(k)) byKey.set(k, []);
      byKey.get(k).push(r);
    });
    return [...byKey.entries()]
      .map(([key, list]) => ({
        key,
        label: labelFor(locations, key),
        list: list.slice().sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
      }))
      .filter(g => g.list.length > 0 || activeLocs.some(l => l.key === g.key));
  }, [rooms, activeLocs, locations]);

  // One room, rendered compactly to fit inside a location box (edit inline).
  const renderRoom = (r) => (
    <div key={r.id} style={{ background: color.surface.card, border: `1px solid ${color.border.default}`, borderRadius: 8, padding: "8px 10px" }}>
      {editId === r.id ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <input style={{ ...s.input, padding: "5px 8px" }} value={editName}
            onChange={e => setEditName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditId(null); }} autoFocus />
          <select style={{ ...s.select, padding: "5px 8px" }} value={editLoc} onChange={e => setEditLoc(e.target.value)}>
            {locOptionsWith(editLoc).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div style={{ display: "flex", gap: 4 }}>
            <button style={{ ...s.ghostBtn, color: color.accent.active, fontWeight: 700 }} onClick={saveEdit}>✓ Save</button>
            <button style={{ ...s.ghostBtn, color: color.text.secondary }} onClick={() => setEditId(null)}>✕</button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ flex: 1, fontWeight: 700, fontSize: 13, opacity: r.isActive !== false ? 1 : 0.5 }}>{r.name}</span>
            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 10,
              background: r.isActive !== false ? "#e8edf9" : color.surface.raised,
              color: r.isActive !== false ? color.accent.primary : color.text.muted }}>
              {r.isActive !== false ? "Active" : "Inactive"}
            </span>
          </div>
          <div style={{ display: "flex", gap: 2, marginTop: 4 }}>
            <button style={{ ...s.ghostBtn, fontSize: 11, padding: "2px 6px" }} onClick={() => startEdit(r)}>Edit</button>
            <button style={{ ...s.ghostBtn, fontSize: 11, padding: "2px 6px", color: r.isActive !== false ? color.accent.closing : color.accent.active }} onClick={() => toggleActive(r)}>
              {r.isActive !== false ? "Deactivate" : "Activate"}
            </button>
            <button style={{ ...s.ghostBtn, fontSize: 11, padding: "2px 6px", color: color.accent.error }} onClick={() => removeRoom(r)}>Remove</button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div>
      <div style={s.tabHeader}>
        <div><div style={s.tabTitle}>Room Management</div><div style={s.tabSub}>Add, edit, or deactivate rooms.</div></div>
        <button style={s.primaryBtn} onClick={() => setShowAdd(v=>!v)}>
          {showAdd ? "✕ Cancel" : "＋ Add Room"}
        </button>
      </div>

      {showAdd && (
        <div style={{...s.panel, marginBottom:16, display:"flex", gap:12, alignItems:"flex-end", flexWrap:"wrap"}}>
          <div style={{flex:1, minWidth:160}}>
            <label style={s.label} htmlFor="rooms-add-name">Room Name</label>
            <input id="rooms-add-name" style={s.input} placeholder="e.g. OR 13, Hybrid OR" value={newName}
              onChange={e=>setNewName(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&handleAddRoom()}/>
          </div>
          <div style={{width:160}}>
            <label style={s.label} htmlFor="rooms-add-location">Location</label>
            <select id="rooms-add-location" style={s.select} value={newLoc || activeLocs[0]?.key || ""} onChange={e=>setNewLoc(e.target.value)}>
              {activeLocs.map(o=><option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          <button style={{...s.primaryBtn, height:38}} onClick={handleAddRoom} disabled={saving}>
            {saving ? "Saving…" : "Add Room"}
          </button>
        </div>
      )}

      {(() => {
        // Plan allowance for this facility.
        //
        // ⚠️ IT COMES FROM THE FACILITY NOW, not from the build.
        // `facilities/{id}.maxRooms` has existed since seed.js and nothing read
        // it, so every tenant on a deployment shared the same number and the
        // only way to change one was to ship a release. The fallback is the
        // constant that used to be hardcoded (raised 20 → 30 on 2026-08-17,
        // because Wesley Chapel runs 24 rooms and the banner was permanently
        // red), so a facility with no `maxRooms` behaves exactly as before.
        //
        // ⚠️ STILL DISPLAY ONLY. Nothing blocks adding a room — this banner
        // informs, it does not enforce, and making it enforce is a product
        // decision about what happens to a hospital mid-day, not a code tidy.
        const ROOM_LIMIT = Number.isFinite(Number(facility?.maxRooms)) && Number(facility?.maxRooms) > 0
          ? Number(facility.maxRooms)
          : DEFAULT_ROOM_LIMIT;
        const over = rooms.length - ROOM_LIMIT;
        const isOver = over > 0;
        return (
          <div style={{
            ...s.panel, padding:"12px 16px", marginBottom:16,
            display:"flex", justifyContent:"flex-end", alignItems:"center", gap:8,
            ...(isOver ? { background:"#fef2f2", border:`1px solid ${color.accent.error}` } : {}),
          }}>
            {isOver && <span style={{fontSize:13}} role="img" aria-label="Warning">⚠️</span>}
            <span style={{fontSize:13, color: isOver ? color.accent.error : color.text.secondary, fontWeight: isOver ? 700 : 400}}>
              {rooms.length} of {ROOM_LIMIT} configured
              {isOver ? ` · ${over} over your plan limit — upgrade or deactivate rooms` : ""}
            </span>
          </div>
        );
      })()}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
        {groups.map(g => (
          <div key={g.key} style={{ background: color.surface.raised, border: `1px solid ${color.border.default}`, borderRadius: 10, padding: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: color.text.muted, marginBottom: 10 }}>
              {g.label} · {g.list.length}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {g.list.map(renderRoom)}
              {g.list.length === 0 && (
                <div style={{ fontSize: 12, color: color.text.muted, padding: "4px 0" }}>No rooms.</div>
              )}
            </div>
          </div>
        ))}
        {groups.length === 0 && (
          <div style={{ textAlign: "center", padding: "30px 0", color: color.text.muted, fontSize: 13 }}>
            No rooms yet — click ＋ Add Room or use 🌱 Setup Rooms on the board.
          </div>
        )}
      </div>
    </div>
  );
}

// ── TAB: USERS ────────────────────────────────
// Stored role keys stay as-is (no data migration); the labels spell out the
// access tier each one maps to. A doctor becomes an "AIC" via the ⭐ toggle.
export const ROLE_OPTIONS = [
  { value: "admin",            label: "Administrator" },
  { value: "anesthesiologist", label: "Doctor (Anesthesiologist)" },
  { value: "provider",         label: "Clinical Staff (CRNA/AA/Resident)" },
  { value: "scheduler",        label: "Scheduler (schedule input only)" },
  { value: "observer",         label: "Observer" },
];
const SUBROLE_OPTIONS = [
  { value: "crna",     label: "CRNA" },
  { value: "aa",       label: "AA" },
  { value: "resident", label: "Resident" },
];

// User-management grouping. Rows are bucketed by role/subRole under section
// headers in this order. Providers with no subRole default to CRNA (matches the
// subRole dropdown's `|| "crna"` default). Admins land last so they're not lost.
const USER_GROUPS = [
  { key: "doctor",   title: "Doctors",        match: (u) => u.role === "anesthesiologist" },
  { key: "crna",     title: "CRNAs",          match: (u) => u.role === "provider" && (u.subRole === "crna" || !u.subRole) },
  { key: "aa",       title: "AAs",            match: (u) => u.role === "provider" && u.subRole === "aa" },
  { key: "resident", title: "Residents",      match: (u) => u.role === "provider" && u.subRole === "resident" },
  { key: "scheduler", title: "Schedulers",    match: (u) => u.role === "scheduler" },
  { key: "observer", title: "Observers",      match: (u) => u.role === "observer" },
  { key: "admin",    title: "Administrators", match: (u) => u.role === "admin" },
];

// Inline schedule-profile editor (Phase 4): shortCode + callOrder for doctors,
// goHomeHour for CRNA/AA. Saves on blur/Enter via the existing updateUser path.
function ScheduleProfileInput({ uid, field, current, label, placeholder, width = 86, numeric = false, uppercase = false, maxLength, onPatch }) {
  const [val, setVal] = useState(current ?? "");
  useEffect(() => { setVal(current ?? ""); }, [current]);

  const save = () => {
    const trimmed = String(val).trim();
    if (trimmed === "") return;
    const next = numeric ? Number(trimmed) : trimmed;
    if (numeric && !Number.isFinite(next)) return;
    if (next === current) return;
    onPatch(uid, { [field]: next }, `${label} updated.`);
  };

  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 10, color: color.text.secondary, fontWeight: 600 }}>
      {label}
      <input
        style={{ ...s.input, width, padding: "5px 8px", fontSize: 12 }}
        value={val}
        placeholder={placeholder}
        maxLength={maxLength}
        inputMode={numeric ? "numeric" : undefined}
        onChange={(e) => setVal(uppercase ? e.target.value.toUpperCase() : e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      />
    </label>
  );
}

export function UsersTab() {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const facilityId = user?.activeFacilityId || "default";
  const [users, setUsers]   = useState([]);
  const [search, setSearch] = useState("");
  const [importing, setImporting] = useState(false);
  // Promoting a user to Administrator grants full app/facility/user/billing
  // control and is hard to undo safely, so it goes through an explicit confirm
  // gate (rather than applying on select like every other role). Holds the
  // { id, name } of the user awaiting promotion, or null when idle.
  const [pendingAdmin, setPendingAdmin] = useState(null);
  // Admin "Add user" — creates a Firebase Auth account + users/{uid} profile
  // without logging the admin out (createUserAccount uses a secondary app).
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [nu, setNu] = useState({ email: "", password: "", firstName: "", lastName: "", role: "provider" });
  const setNuField = (k, v) => setNu((p) => ({ ...p, [k]: v }));
  // Bulk login-account creation for seeded profiles (no Auth account yet).
  const [showBulk, setShowBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProg, setBulkProg] = useState(null); // { done, total } while running
  // "Fix emails" — recompute non-admin emails to the canonical domain.
  const [showFixEmail, setShowFixEmail] = useState(false);
  const [fixingEmail, setFixingEmail] = useState(false);
  // "Link staff to accounts" — stamp staff.uid for the whole roster so messaging
  // channels + dropdowns resolve everyone, not just people who've logged in.
  const [linkBusy, setLinkBusy] = useState(false);
  const handleLinkStaff = async () => {
    setLinkBusy(true);
    try {
      const r = await linkStaffToAccounts();
      toast.success(`Linked ${r.linked} staff to accounts (${r.alreadyLinked} already linked, ${r.unmatched} no match).`);
    } catch (err) {
      console.error("Link staff failed:", err);
      toast.error("Could not link staff. Admin rights required.");
    } finally {
      setLinkBusy(false);
    }
  };

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "users"), where("facilityIds", "array-contains", facilityId)),
      (snap) => setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => { console.error("Load users failed:", err); toast.error("Could not load users."); }
    );
    return unsub;
  }, [facilityId, toast]);

  // ⚠️ THE DIRECTORY, SO THIS SCREEN CAN SAY WHO IS MISSING FROM IT. An account
  // with no `staff` record can sign in and is invisible to every board picker —
  // the failure reported live on 2026-09-11. Without this list the screen cannot
  // tell the admin which of their accounts are in that state.
  const [staffDocs, setStaffDocs] = useState([]);
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "staff"),
      (snap) => setStaffDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Load staff failed:", err)
    );
    return unsub;
  }, []);

  // Is this login already a person the board can see? Matched on the uid LINK
  // first — that is the only exact join — then on email, which is how
  // `linkStaffToAccounts` matches and so keeps the two screens agreeing.
  const staffByUid = useMemo(() => {
    const byUid = new Set();
    const byEmail = new Set();
    for (const st of staffDocs) {
      if (st.uid) byUid.add(st.uid);
      const em = String(st.email || "").trim().toLowerCase();
      if (em) byEmail.add(em);
    }
    return { byUid, byEmail };
  }, [staffDocs]);

  const missingFromDirectory = (u) => {
    const role = staffRoleForProfile({ role: u.role, subRole: u.subRole });
    if (!role) return null; // admin, scheduler, observer, board device
    if (staffByUid.byUid.has(u.id)) return null;
    const em = String(u.email || "").trim().toLowerCase();
    if (em && staffByUid.byEmail.has(em)) return null;
    return role;
  };

  // ⚠️ ONE PERSON, ONE DELIBERATE TAP — NEVER A BULK SWEEP. The product ruling
  // is "never create — always ask a human", and the reason is twenty-three
  // duplicate colleagues: an account whose staff record exists under a
  // different spelling looks "missing" to any automatic matcher, and creating
  // it would file the same clinician twice. An admin reads the name and decides.
  const [addingId, setAddingId] = useState(null);
  const addToDirectory = async (u) => {
    const role = missingFromDirectory(u);
    if (!role) return;
    setAddingId(u.id);
    try {
      await addStaff({
        name: u.name || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email,
        email: u.email || "",
        uid: u.id,
        role,
        primaryFacilityId: facilityId,
      });
      toast.success(`${u.name || u.email} is now in the Team directory and can be rostered.`);
    } catch (err) {
      console.error("Add to directory failed:", err);
      toast.error("Could not add them to the directory. Admin rights required.");
    } finally {
      setAddingId(null);
    }
  };

  const patchUser = async (uid, data, successMsg) => {
    try {
      await updateUser(uid, data);
      // ⚠️ A ROLE EDIT MUST REACH `staff`, OR IT CHANGES NOTHING A USER SEES.
      // This wrote only `users`. The Team directory files people by
      // `staff.role` and the board's assignable pool reads the same field, so
      // an admin could switch somebody CRNA→AA, see the form save, and watch
      // the directory keep them under CRNAs forever. Nine people were mismatched
      // when this was found (2026-08-20).
      //
      // Only fires when the patch actually touches the role, and only for a
      // profile that describes a rosterable person — staffRoleForProfile
      // returns null for an admin, a scheduler or a board device.
      if ("role" in data || "subRole" in data) {
        const current = users.find((u) => u.id === uid) || {};
        const staffRole = staffRoleForProfile({
          role: "role" in data ? data.role : current.role,
          subRole: "subRole" in data ? data.subRole : current.subRole,
        });
        if (staffRole) {
          try {
            await mirrorRoleToStaff(uid, staffRole);
          } catch (mirrorErr) {
            // The profile write already succeeded; failing the whole save here
            // would report "could not update" about a change that happened.
            console.error("Role mirror to staff failed:", mirrorErr);
            toast.error("Saved — but the Team directory could not be updated. Tell an admin.");
          }
        }
      }
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      console.error("Update user failed:", err);
      toast.error("Could not update the user. Admin rights are required.");
    }
  };

  const handleImportShortCodes = async () => {
    setImporting(true);
    try {
      // Seed BOTH collections: users (legacy profile docs) and staff (the
      // operational board pool that useStaff/imports read). Without the staff
      // pass, board imports can't resolve attending short codes.
      const u = await seedProviderCodes(facilityId);
      const s = await seedStaffCodes();
      const needsReview = u.unmatched + u.ambiguous + s.unmatched + s.ambiguous;
      const msg = `Short codes: users ${u.applied} applied/${u.skipped} set, staff ${s.applied} applied/${s.skipped} set${needsReview > 0 ? `, ${needsReview} need manual review (see console)` : ""}.`;
      toast.success(msg);
    } catch (err) {
      console.error("Import short codes failed:", err);
      toast.error("Could not import short codes.");
    } finally {
      setImporting(false);
    }
  };

  const filtered = users.filter((u) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  // ── Who is on the app right now ──────────────
  // Presence is derived from users/{uid}.lastSeenAt, which AuthContext stamps
  // while the app is in the foreground. Ticking `now` in state (rather than
  // reading Date.now() during render) is what makes "12m ago" actually age
  // without the admin reloading the page.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(t);
  }, []);
  const presence = useMemo(() => summarizePresence(filtered, now), [filtered, now]);

  // Provider role chip: purple (#7c3aed) has no token equivalent — kept as literal.
  const roleColor = (role) =>
    ({ admin: color.accent.closing, anesthesiologist: color.accent.primary, provider: "#7c3aed", observer: color.text.muted }[role] || color.text.secondary);

  const canBeAIC = (u) => u.role === "anesthesiologist" || u.role === "admin";

  // Role-select gate: a change TO admin (from a non-admin role) is deferred to
  // the confirm modal; every other role change applies immediately as before.
  const handleRoleChange = (u, value) => {
    if (value === "admin" && u.role !== "admin") {
      setPendingAdmin({ id: u.id, name: u.name });
      return;
    }
    patchUser(u.id, { role: value }, "Role updated.");
  };

  const confirmPromotion = () => {
    if (!pendingAdmin) return;
    patchUser(pendingAdmin.id, { role: "admin" }, `${pendingAdmin.name} is now an Administrator.`);
    setPendingAdmin(null);
  };

  // How many listed profiles still need a login account.
  const pendingAccountCount = users.filter(needsAccount).length;

  // Non-admin users whose stored email differs from the canonical derivation.
  const emailFixTargets = users.filter((u) => {
    if (u.role === "admin") return false;
    const want = staffEmail(u.firstName, u.lastName);
    return want && u.email !== want;
  });

  const handleFixEmails = async () => {
    setFixingEmail(true);
    let fixed = 0;
    try {
      for (const u of emailFixTargets) {
        await updateUser(u.id, { email: staffEmail(u.firstName, u.lastName) });
        fixed += 1;
      }
      toast.success(`${fixed} email(s) updated to @${staffEmail("a", "b").split("@")[1]}.`);
      setShowFixEmail(false);
    } catch (err) {
      console.error("Fix emails failed:", err);
      toast.error("Could not update all emails. Admin rights required; re-run to finish.");
    } finally {
      setFixingEmail(false);
    }
  };

  const handleBulkCreate = async () => {
    setBulkBusy(true);
    setBulkProg({ done: 0, total: pendingAccountCount });
    try {
      const res = await bulkCreateLoginAccounts(users, (p) => setBulkProg(p));
      const failNote = res.failed.length ? ` ${res.failed.length} failed (see console).` : "";
      if (res.failed.length) console.warn("Bulk account failures:", res.failed);
      toast.success(`${res.created} login account(s) created, ${res.skipped} skipped.${failNote} Users set their password via "Forgot password" on the login page.`);
      setShowBulk(false);
    } catch (err) {
      console.error("Bulk create failed:", err);
      toast.error("Bulk account creation failed. Some accounts may have been created — re-run to finish.");
    } finally {
      setBulkBusy(false);
      setBulkProg(null);
    }
  };

  const handleCreateUser = async () => {
    const email = nu.email.trim().toLowerCase();
    if (!email || !nu.password) { toast.error("Email and a temporary password are required."); return; }
    if (nu.password.length < 6) { toast.error("Temporary password must be at least 6 characters."); return; }
    setCreating(true);
    try {
      const uid = await createUserAccount(email, nu.password);
      const name = `${nu.firstName.trim()} ${nu.lastName.trim()}`.trim() || email.split("@")[0];
      await createUser(uid, {
        uid, email, name,
        firstName: nu.firstName.trim(), lastName: nu.lastName.trim(),
        phone: "", address: "", shortCode: "",
        role: nu.role, subRole: nu.role === "provider" ? "crna" : null,
        facilityIds: [facilityId], activeFacilityId: facilityId,
        activeFacilityName: user?.activeFacilityName || "",
        isAIC: false, status: "active",
        currentRoomId: null, currentRoomName: null, fcmToken: null, packageTier: "basic",
      });
      // ⚠️ AN ACCOUNT IS NOT A PERSON, AND THE BOARD ONLY KNOWS PEOPLE.
      // `users` models LOGINS; `staff` models the colleagues a roster, an
      // `ahpId` and an `attendingId` all point at. Creating a user here used to
      // write the login and stop — so the new doctor could sign in, and was
      // invisible to the Team directory, to "Add a provider", and to every
      // picker on the board. Reported live on 2026-09-11: an account created
      // that morning could not be put on the day's board at all.
      //
      // `staffRoleForProfile` returns null for an admin, a scheduler, an
      // observer or a board device — none of those is a rosterable person, so
      // none of them gets a directory record.
      const staffRole = staffRoleForProfile({
        role: nu.role,
        subRole: nu.role === "provider" ? "crna" : null,
      });
      let directoryNote = "";
      if (staffRole) {
        try {
          // Linked by `uid` at birth: that link is what lets a directive, an
          // alert and a push find them later. A staff record with uid null is
          // pool-only — it can be rostered but can never be told anything.
          await addStaff({
            name, email, uid,
            role: staffRole,
            primaryFacilityId: facilityId,
          });
          directoryNote = " Added to the Team directory, so they can be rostered and assigned.";
        } catch (staffErr) {
          // ⚠️ SAY SO. A silent failure here is the whole reported bug: the
          // account exists, the person does not, and nothing on screen said
          // which half landed.
          console.error("Create staff record failed:", staffErr);
          directoryNote =
            " ⚠️ The login was created but the Team directory record was NOT — they cannot be put on the board yet. Try again or add them from Setup → Users.";
        }
      }
      toast.success(`Account created for ${email}. They sign in with the temporary password, then change it in My Profile.${directoryNote}`);
      setShowAdd(false);
      setNu({ email: "", password: "", firstName: "", lastName: "", role: "provider" });
    } catch (err) {
      console.error("Create user failed:", err);
      const code = err?.code || "";
      if (code === "auth/email-already-in-use") toast.error("That email already has an account.");
      else if (code === "auth/invalid-email") toast.error("That email address is not valid.");
      else if (code === "auth/weak-password") toast.error("Temporary password must be at least 6 characters.");
      else toast.error("Could not create the account.");
    } finally {
      setCreating(false);
    }
  };

  // One user row. Only doctors keep the avatar circle, and it shows their short
  // code (falls back to name initials until a code is assigned). All other roles
  // render without a leading circle.
  const renderUserRow = (u) => {
    const roleClr = roleColor(u.role);
    const isDoctor = u.role === "anesthesiologist";
    const code = (u.shortCode || "").toUpperCase() || (u.name || u.email || "?").slice(0, 2).toUpperCase();
    const self = u.id === user.uid;
    return (
      <div key={u.id} style={s.userRow}>
        {isDoctor && (
          <div style={{...s.pav, background:`${roleClr}22`, color:roleClr, border:`1px solid ${roleClr}`, width:36, height:36}}>{code}</div>
        )}
        <div style={{flex:1, minWidth:140}}>
          <div style={{fontSize:13, fontWeight:700}}>{u.name}{self ? " (you)" : ""}{u.isAIC ? " ⭐" : ""}</div>
          <div style={{fontSize:11, color:color.text.secondary}}>{u.email}</div>
          {/* ⚠️ AN ACCOUNT THAT IS NOT A PERSON. `users` is a login; the board
              only knows `staff`. Until this said so, an admin created an
              account, saw it listed here, and had no way to learn why the new
              doctor never appeared in "Add a provider" or on the roster.
              Amber, not red: nothing is broken, something is unfinished. */}
          {missingFromDirectory(u) && (
            <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: color.status.closing.text }}>
                Not in the Team directory — cannot be rostered or assigned yet
              </span>
              <button
                type="button"
                style={{ ...s.outlineBtn, padding: "4px 10px", fontSize: 12, minHeight: 28 }}
                disabled={addingId === u.id}
                onClick={() => addToDirectory(u)}
              >
                {addingId === u.id ? "Adding…" : "Add to directory"}
              </button>
            </div>
          )}
        </div>
        {isAdmin ? (
          <>
            <select aria-label={`Role for ${u.name}`} style={{...s.select, width:160}} value={u.role || "provider"}
              disabled={self}
              onChange={(e) => handleRoleChange(u, e.target.value)}>
              {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {u.role === "provider" && (
              <select aria-label={`Provider type for ${u.name}`} style={{...s.select, width:120}} value={u.subRole || "crna"}
                onChange={(e) => patchUser(u.id, { subRole: e.target.value }, "Provider type updated.")}>
                {SUBROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
            {u.role === "anesthesiologist" && (
              <>
                <ScheduleProfileInput uid={u.id} field="shortCode" current={u.shortCode}
                  label="Short Code" placeholder="e.g. TAN" maxLength={5} uppercase width={72} onPatch={patchUser} />
                <ScheduleProfileInput uid={u.id} field="callOrder" current={u.callOrder}
                  label="Call Order #" placeholder="1" numeric width={64} onPatch={patchUser} />
              </>
            )}
            {u.role === "provider" && (u.subRole === "crna" || u.subRole === "aa" || !u.subRole) && (
              <>
                <ScheduleProfileInput uid={u.id} field="shiftCode" current={u.shiftCode}
                  label="Shift Code" placeholder="e.g. 5 or 11a-7p" width={110} onPatch={patchUser} />
                <ScheduleProfileInput uid={u.id} field="goHomeHour" current={u.goHomeHour}
                  label="Go-Home Hour" placeholder="e.g. 5 = 1700" numeric width={92} onPatch={patchUser} />
              </>
            )}
            <button
              style={{...s.ghostBtn, color: u.isAIC ? color.accent.closing : color.text.secondary, opacity: canBeAIC(u) ? 1 : 0.4}}
              aria-pressed={!!u.isAIC}
              disabled={!canBeAIC(u)}
              title={canBeAIC(u) ? "Toggle Anesthesiologist In Charge" : "Only doctors/admins can be AIC"}
              onClick={() => patchUser(u.id, { isAIC: !u.isAIC }, u.isAIC ? "AIC removed." : `${u.name} is now AIC.`)}>
              {u.isAIC ? "★ AIC" : "☆ Make AIC"}
            </button>
          </>
        ) : (
          <div style={{fontSize:11, fontWeight:600, padding:"3px 9px", borderRadius:10, background:`${roleClr}22`, color:roleClr}}>
            {u.role}{u.subRole ? " · " + u.subRole.toUpperCase() : ""}{u.isAIC ? " · AIC" : ""}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={s.tabHeader}>
        <div><div style={s.tabTitle}>User Management</div><div style={s.tabSub}>Assign roles and designate AIC. Users appear here after their first sign-in.</div></div>
        <div style={{display:"flex", gap:8}}>
          {isAdmin && (
            <button
              style={s.primaryBtn}
              onClick={handleImportShortCodes}
              disabled={importing}
              title="Applies official 3-letter codes to matched anesthesiologist accounts. Unmatched codes are reported to the console."
            >
              {importing ? "Importing…" : "Import official short codes"}
            </button>
          )}
          {isAdmin && emailFixTargets.length > 0 && (
            <button style={s.primaryBtn} onClick={() => setShowFixEmail(true)}>
              Fix emails ({emailFixTargets.length})
            </button>
          )}
          {isAdmin && pendingAccountCount > 0 && (
            <button style={s.primaryBtn} onClick={() => setShowBulk(true)}>
              Create login accounts ({pendingAccountCount})
            </button>
          )}
          {isAdmin && (
            <button
              style={s.primaryBtn}
              onClick={handleLinkStaff}
              disabled={linkBusy}
              title="Link every staff record to its login account (by email) so messaging channels and the message dropdowns include the whole roster, not just people who've signed in."
            >
              {linkBusy ? "Linking…" : "Link staff to accounts"}
            </button>
          )}
          {isAdmin && (
            <button style={s.primaryBtn} onClick={() => setShowAdd(true)}>＋ Add user</button>
          )}
        </div>
      </div>
      {isAdmin && (
        <div style={s.panel}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: color.text.primary }}>
              Signed in now
            </div>
            <div style={{ fontSize: 12, color: color.text.secondary }} aria-live="polite">
              <strong style={{ color: color.accent.active }}>{presence.onlineCount}</strong> online
              {" · "}{presence.total} with access
              {presence.neverSignedInCount > 0 && (
                <> · <strong>{presence.neverSignedInCount}</strong> never signed in</>
              )}
            </div>
          </div>
          <p style={{ fontSize: 11, color: color.text.muted, margin: "0 0 10px" }}>
            Who has the app open right now, for this facility. Updates while the app is
            in the foreground, so someone who closed it drops off within about ten
            minutes. Nothing about what they are doing is recorded.
          </p>
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {presence.rows.length === 0 && (
              <div style={{ fontSize: 13, color: color.text.muted, padding: "8px 0" }}>
                No users match.
              </div>
            )}
            {presence.rows.map((r) => (
              <div
                key={r.id}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "6px 0", borderBottom: `1px solid ${color.border.default}`,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                    background: r.online ? color.accent.active : color.text.muted,
                    opacity: r.online ? 1 : 0.4,
                  }}
                />
                <span style={{ fontSize: 13, color: color.text.primary, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.name || r.email || r.id}
                </span>
                {/* Colour alone never carries the state — the text says it too. */}
                <span style={{ fontSize: 12, color: r.online ? color.accent.active : color.text.muted, whiteSpace: "nowrap" }}>
                  {r.online ? "online" : r.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={s.panel}>
        <label style={s.label} htmlFor="user-search">Search</label>
        <input id="user-search" style={{...s.input, marginBottom:16}} placeholder="Search by name or email..."
          value={search} onChange={(e) => setSearch(e.target.value)} />

        {filtered.length === 0 && (
          <div style={{textAlign:"center", padding:"24px 0", color:color.text.muted, fontSize:13}}>No matching users.</div>
        )}

        {USER_GROUPS.map((g) => {
          const members = filtered.filter(g.match);
          if (members.length === 0) return null;
          return (
            <div key={g.key}>
              <div style={{fontSize:12, fontWeight:700, textTransform:"uppercase", letterSpacing:0.4, color:color.text.muted, padding:"14px 0 4px", borderBottom:`2px solid ${color.border.default}`}}>
                {g.title} <span style={{fontWeight:600, color:color.text.secondary}}>({members.length})</span>
              </div>
              {members.map(renderUserRow)}
            </div>
          );
        })}
      </div>

      <Modal
        isOpen={showFixEmail}
        onClose={() => !fixingEmail && setShowFixEmail(false)}
        title={`Update ${emailFixTargets.length} email(s)?`}
        footer={
          <>
            <button style={s.ghostBtn} onClick={() => setShowFixEmail(false)} disabled={fixingEmail}>Cancel</button>
            <button style={s.primaryBtn} onClick={handleFixEmails} disabled={fixingEmail}>
              {fixingEmail ? "Updating…" : `Update ${emailFixTargets.length} emails`}
            </button>
          </>
        }
      >
        <p style={{margin:"0 0 10px", fontSize:13, color:color.text.secondary}}>
          Rewrites every non-administrator email to <strong>firstname.lastname@envisionhealth.com</strong>
          {" "}(derived from each person's name). Administrators are left unchanged.
        </p>
        <p style={{margin:0, fontSize:12, color:color.text.muted}}>
          This updates the profile email only. If a person already has a login account, their
          sign-in email won't change until accounts are recreated for the new address.
        </p>
      </Modal>

      <Modal
        isOpen={showBulk}
        onClose={() => !bulkBusy && setShowBulk(false)}
        title={`Create ${pendingAccountCount} login account(s)?`}
        footer={
          <>
            <button style={s.ghostBtn} onClick={() => setShowBulk(false)} disabled={bulkBusy}>Cancel</button>
            <button style={s.primaryBtn} onClick={handleBulkCreate} disabled={bulkBusy}>
              {bulkBusy
                ? `Creating… ${bulkProg ? `${bulkProg.done}/${bulkProg.total}` : ""}`
                : `Create ${pendingAccountCount} accounts`}
            </button>
          </>
        }
      >
        <p style={{margin:"0 0 10px", fontSize:13, color:color.text.secondary}}>
          Creates a sign-in account for every listed user who doesn't have one yet, using
          their email as the login. <strong>No emails are sent.</strong> Each person goes to
          the login page, clicks <strong>“Forgot password”</strong>, and sets their own
          password from the reset link.
        </p>
        <p style={{margin:0, fontSize:12, color:color.text.muted}}>
          Safe to re-run — people who already have an account are skipped. Large lists may
          hit a temporary rate limit; just run it again to finish.
        </p>
      </Modal>

      <Modal
        isOpen={showAdd}
        onClose={() => !creating && setShowAdd(false)}
        title="Add a user"
        footer={
          <>
            <button style={s.ghostBtn} onClick={() => setShowAdd(false)} disabled={creating}>Cancel</button>
            <button style={s.primaryBtn} onClick={handleCreateUser} disabled={creating}>
              {creating ? "Creating…" : "Create account"}
            </button>
          </>
        }
      >
        <p style={{margin:"0 0 14px", fontSize:13, color:color.text.secondary}}>
          Creates the sign-in account and profile. Give them the temporary password — they
          change it in <strong>My Profile</strong> after their first sign-in. You stay signed in.
        </p>
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10}}>
          <Field id="nu-first" label="First name" value={nu.firstName}
            onChange={(e) => setNuField("firstName", e.target.value)} placeholder="Jane" />
          <Field id="nu-last" label="Last name" value={nu.lastName}
            onChange={(e) => setNuField("lastName", e.target.value)} placeholder="Doe" />
        </div>
        <Field id="nu-email" label="Email" type="email" value={nu.email}
          onChange={(e) => setNuField("email", e.target.value)} placeholder="jane@hospital.com" required />
        <Field id="nu-pw" label="Temporary password" type="text" value={nu.password}
          onChange={(e) => setNuField("password", e.target.value)} placeholder="At least 6 characters"
          helperText="Share this with the user; they change it after signing in." required />
        <label style={s.label} htmlFor="nu-role">Role</label>
        <select id="nu-role" style={s.select} value={nu.role}
          onChange={(e) => setNuField("role", e.target.value)}>
          {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Modal>

      <Modal
        isOpen={!!pendingAdmin}
        onClose={() => setPendingAdmin(null)}
        title="Promote to Administrator?"
        footer={
          <>
            <button style={s.ghostBtn} onClick={() => setPendingAdmin(null)}>Cancel</button>
            <button style={{...s.primaryBtn, background: color.accent.closing}} onClick={confirmPromotion}>
              Promote to Admin
            </button>
          </>
        }
      >
        <p style={{margin:0}}>
          <strong>{pendingAdmin?.name}</strong> will gain full Administrator access — app and
          facility setup, room and user management, role changes, and billing. They can also
          promote other users. This takes effect immediately.
        </p>
      </Modal>
    </div>
  );
}

// ── TAB: PACKAGE ──────────────────────────────
export function PackageTab() {
  const plans = [
    {name:"Basic",   price:"$49",  current:false, features:["3 rooms · 1 facility","Full board","Break tracking","Emergency alerts"], missing:["Reports","AI insights"]},
    {name:"Premium", price:"$199", current:true,  features:["20 rooms · 3 facilities","Full board","Break tracking","Emergency alerts","Reports & audit log"], missing:["AI insights"]},
    {name:"Ultra",   price:"$399", current:false, features:["20 rooms · 5 facilities","Full board","Break tracking","Emergency alerts","Reports & audit log","AI insights (Gemini)"], missing:[]},
  ];
  return (
    <div>
      <div style={s.tabHeader}>
        <div><div style={s.tabTitle}>Package & Billing</div><div style={s.tabSub}>Manage your subscription.</div></div>
      </div>
      <div style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14}}>
        {plans.map(p => (
          <div key={p.name} style={{...s.panel, ...(p.current ? {border:`1px solid ${color.accent.closing}`, background:`${color.accent.closing}08`} : {})}}>
            {p.current && <div style={{fontSize:10, fontWeight:700, color:color.accent.closing, marginBottom:6, textTransform:"uppercase"}}>Current Plan</div>}
            <div style={{fontSize:18, fontWeight:800, color: p.current ? color.accent.closing : color.text.primary}}>{p.name}</div>
            <div style={{fontSize:26, fontWeight:900, margin:"6px 0 16px", color: p.current ? color.accent.closing : color.text.primary}}>
              {p.price}<span style={{fontSize:13, fontWeight:400, color:color.text.secondary}}>/mo</span>
            </div>
            <div style={{display:"flex", flexDirection:"column", gap:7, marginBottom:20}}>
              {p.features.map(f => <div key={f} style={{fontSize:12, color:color.text.secondary}}>✓ {f}</div>)}
              {p.missing.map(f => <div key={f} style={{fontSize:12, color:color.text.muted, opacity:.5}}>✗ {f}</div>)}
            </div>
            {p.current
              ? <button style={{...s.outlineBtn, width:"100%", opacity:.4}} disabled>Active Plan</button>
              : <button style={{...(p.name==="Ultra" ? s.primaryBtn : s.outlineBtn), width:"100%", justifyContent:"center"}}>
                  {p.name==="Ultra" ? "Upgrade →" : "Downgrade"}
                </button>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── TAB: NOTIFICATIONS ────────────────────────
export function NotificationsTab() {
  const [settings, setSettings] = useState({
    emergencyPush:true, emergencySound:true, emergencySMS:true,
    urgencyPush:true, urgencyMessage:true,
    breakNotifyAIC:true, breakNotifyProvider:true, breakOvertime:false,
    boardChangeAIC:true, boardChangeProvider:true, dailySummary:false,
  });
  const toggle = (key) => setSettings(prev => ({...prev, [key]:!prev[key]}));
  const Toggle = ({k, label}) => (
    <button
      style={{...s.toggle, ...(settings[k] ? s.toggleOn : {})}}
      role="switch"
      aria-checked={settings[k]}
      aria-label={label}
      onClick={() => toggle(k)}>
      <div style={{...s.toggleKnob, ...(settings[k] ? s.toggleKnobOn : {})}}/>
    </button>
  );

  const Section = ({title, rows}) => (
    <div style={{...s.panel, marginBottom:14}}>
      <div style={s.panelTitle}>{title}</div>
      {rows.map(r => (
        <div key={r.k} style={s.notifRow}>
          <div><div style={{fontSize:13, fontWeight:600}}>{r.label}</div><div style={{fontSize:11, color:color.text.secondary, marginTop:3}}>{r.sub}</div></div>
          <Toggle k={r.k} label={r.label}/>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <div style={s.tabHeader}>
        <div><div style={s.tabTitle}>Notification Settings</div><div style={s.tabSub}>Control how alerts are delivered.</div></div>
        <button style={s.primaryBtn}>💾 Save</button>
      </div>
      <div style={{maxWidth:700}}>
        <Section title="🚨 Emergency Alerts" rows={[
          {k:"emergencyPush",  label:"Push notification", sub:"Sent to all available doctors immediately"},
          {k:"emergencySound", label:"Sound alert",       sub:"Play alarm on all connected devices"},
          {k:"emergencySMS",   label:"SMS fallback",      sub:"SMS if push notification fails"},
        ]}/>
        <Section title="⚡ Urgency Alerts" rows={[
          {k:"urgencyPush",    label:"Push notification", sub:"Sent to supervising doctor only"},
          {k:"urgencyMessage", label:"In-app message",    sub:"Creates a message thread with context"},
        ]}/>
        <Section title="☕ Break Requests" rows={[
          {k:"breakNotifyAIC",      label:"Notify AIC on request",          sub:"AIC gets push + in-app notification"},
          {k:"breakNotifyProvider", label:"Notify provider on approval",     sub:"Provider is notified of AIC decision"},
          {k:"breakOvertime",       label:"Break overtime alert",            sub:"Alert if break exceeds estimated time"},
        ]}/>
        <Section title="📋 Board Changes" rows={[
          {k:"boardChangeAIC",      label:"Notify AIC on non-AIC board change", sub:"Keeps AIC informed of all modifications"},
          {k:"boardChangeProvider", label:"Notify provider on assignment change",sub:"Push when provider is moved to new room"},
          {k:"dailySummary",        label:"Daily board summary",               sub:"AIC receives end-of-day summary"},
        ]}/>
      </div>
    </div>
  );
}
